import { hostBridge } from '@publicala/delfino';
import type { Bookmark, BookmarkCreatePayload, BookmarkLocation, SerializedLocator } from '@publicala/delfino';
import { DEFAULT_BOOKMARKS_LIST_MOCK, createMockBookmark, isE2EMockActive } from '../e2eMockGuard';

/**
 * Flat bookmark shape coming from bookmarkModel (`{...this}`): a page plus, for EPUB,
 * the serialized RWPM locator. Transformed into the nested Delfino payload before sending.
 */
interface VolpeBookmarkPayload {
    uuid: string;
    type: 'pdf' | 'epub';
    page: number;
    locator?: SerializedLocator | null;
}

/**
 * Bookmarks API for Volpe. Wraps hostBridge.bookmarks with E2E-mock short-circuits.
 */
class BookmarksAPI {
    async store(payload: VolpeBookmarkPayload): Promise<Bookmark> {
        const createPayload: BookmarkCreatePayload = {
            uuid: payload.uuid,
            location: this.buildLocation(payload),
        };

        if (isE2EMockActive()) {
            return createMockBookmark(createPayload);
        }

        return hostBridge.bookmarks.store(createPayload);
    }

    async delete(bookmarkId: string): Promise<void> {
        if (isE2EMockActive()) {
            return;
        }

        return hostBridge.bookmarks.delete({ bookmarkId });
    }

    async list(issueId?: string): Promise<Bookmark[]> {
        if (isE2EMockActive()) {
            return DEFAULT_BOOKMARKS_LIST_MOCK;
        }

        return hostBridge.bookmarks.list({ issueId });
    }

    /**
     * The format comes from the payload; only the locator is conditional. Inferring the format
     * from the locator's presence made a locator-less EPUB bookmark leave here as `type: 'pdf'`,
     * and it comes back from the host that way — where `refreshCurrentPageBookmark` matches it
     * through the PDF branch, comparing a Readium position against a printed page number.
     *
     * Unreachable today: `buildBookmarkAtCurrentLocation` returns null before an EPUB bookmark
     * can get here without a locator. Which is the reason not to encode a guess in the fallback —
     * the day that changes, a degraded EPUB bookmark should stay an EPUB bookmark.
     */
    private buildLocation(payload: VolpeBookmarkPayload): BookmarkLocation {
        const location: BookmarkLocation = { type: payload.type, page: payload.page };

        // `locator` is EPUB-only in the Delfino contract, and optional rather than nullable.
        if (payload.type === 'epub' && payload.locator) {
            location.locator = payload.locator;
        }

        return location;
    }
}

export const bookmarksAPI = new BookmarksAPI();
